import { chooseMission } from '../core/game/mission-director.js';
import { deriveSeed, mulberry32 } from '../core/world/rand.js';
import { ui } from '../core/tunables.js';
import { iconElement } from '../content/icons.js';
import { MISSIONS } from './placeholder-content.js';
import { routeForMission } from './mission-route.js';
import { backPaw, bigButton, el, GLYPH, GLYPH_ICON, timeout } from './dom.js';
import type { Screen, ScreenContext } from './router.js';

/**
 * The PupPad call: "go and fetch this".
 *
 * Not one word anywhere. A tablet rings, an icon appears in a bubble, and a big green tick sends her
 * on her way. Two details matter more than the animation:
 *
 *  - it **accepts itself** after a few seconds, so she can never be stranded on a screen whose meaning
 *    she has not worked out;
 *  - the mission is chosen once, at mount, and held. An earlier version recomputed it for the button
 *    and for the timer separately, which meant the two could disagree about what she had accepted.
 */

export interface BriefingScreenOptions {
  /** Injected for tests, which have no network for the icon fetch. */
  readonly loadIcon?: (id: string) => Promise<SVGSVGElement | null>;
  readonly autoAcceptSeconds?: number;
}

export function createBriefingScreen(options: BriefingScreenOptions = {}): Screen {
  const loadIcon = options.loadIcon ?? iconElement;
  const autoAcceptSeconds = options.autoAcceptSeconds ?? ui.briefingAutoAcceptSeconds;

  let disposed = false;
  const disposers: (() => void)[] = [];

  return {
    mount(root, ctx) {
      const mission = pickMission(ctx);
      /*
       * The seed for the whole world, decided once, here.
       *
       * Derived from the same thing the mission is - `routeCounter`, how many drives she has been sent on -
       * so a screen that remounts offers the same drive rather than quietly swapping it for another. It
       * travels in the loop state from this point on, which is what makes the way home the same place as
       * the way out.
       *
       * The counter, not `missionHistory.length`: see the note on `SaveData.routeCounter`. Reading a number
       * that only moves on a *completed* mission meant an abandoned drive was repeated exactly.
       */
      const routeSeed = deriveSeed(ctx.save.routeCounter + 1, 'mission-route');

      /*
       * How many things are on that road, found by growing the road.
       *
       * Not by asking a cheaper function "how many items would a route from this seed have" - that is
       * two implementations of one number, and the way they fail is she is told to fetch four things
       * when three were placed, so the trailer never fills and the only way home is the back paw.
       * `routeForMission` is the single place a route comes from, and the drive screen calls the same
       * function with the same arguments. A few hundred heightfield samples is a fair price for that.
       */
      const itemCount = mission === null
        ? 1
        : routeForMission(mission.id, routeSeed, ctx.save).items.length;

      /*
       * Sending her off is what spends the seed, so the next briefing is somewhere else - whether or not
       * this drive is ever finished. Guarded because the tick and the auto-accept are two paths to the same
       * place, and the counter must move exactly once however she leaves.
       */
      let sent = false;
      const send = (): void => {
        if (mission === null || sent) return;
        sent = true;
        ctx.updateSave((save) => ({ ...save, routeCounter: save.routeCounter + 1 }));
        ctx.dispatch({ type: 'BRIEFING_ACCEPTED', missionId: mission.id, routeSeed, itemCount });
      };

      /*
       * The bubble shows the thing once per thing she is fetching.
       *
       * Three balls in the bubble is the only way to say "three of these" without a numeral. It is the
       * same picture as the row on the driving HUD and the same picture as the load in the trailer, so
       * the three places she sees the errand all say it the same way.
       */
      const bubble = el('div', {
        className: itemCount > 1 ? 'puppad-bubble puppad-bubble--row' : 'puppad-bubble',
      });
      const accept = bigButton(GLYPH.confirm, send, {
        variant: 'go',
        className: 'puppad-accept',
        icon: GLYPH_ICON.confirm,
      });

      const screen = el('div', {
        className: 'screen screen--briefing',
        children: [
          backPaw(() => ctx.dispatch({ type: 'BACK' })),
          el('div', {
            className: 'puppad',
            children: [
              // Concentric rings pulsing outward: the visual language of a device ringing, which she
              // will have seen on every phone in the house.
              el('div', { className: 'puppad-ring' }),
              el('div', { className: 'puppad-ring puppad-ring--delayed' }),
              el('div', {
                className: 'puppad-frame',
                children: [el('div', { className: 'puppad-badge', text: GLYPH.back }), bubble],
              }),
            ],
          }),
          el('div', { className: 'puppad-actions', children: [accept] }),
        ],
      });
      root.append(screen);

      if (mission !== null) {
        void loadIcon(mission.itemId).then((icon) => {
          if (disposed || icon === null) return;
          // One fetch, cloned per slot: the loader caches by id, but cloning is what stops all three
          // slots holding the same element and therefore showing one icon.
          const icons = Array.from({ length: itemCount }, (_unused, i) =>
            i === 0 ? icon : (icon.cloneNode(true) as SVGSVGElement),
          );
          bubble.replaceChildren(...icons);
        });

        disposers.push(timeout(autoAcceptSeconds * 1000, send));
      }
    },

    unmount() {
      disposed = true;
      for (const dispose of disposers) dispose();
      disposers.length = 0;
    },
  };
}

/**
 * Deterministic per route counter, so the same briefing is offered if the screen remounts.
 * `chooseMission` avoids the recently-played ones, so she does not get the ball three times running.
 *
 * Driven by the same counter as the seed, so an abandoned drive changes what she is sent to fetch and what
 * sort of place it is in - the recipe carried on the mission is what picks the biomes and how wild the
 * ground may get. Rerolling the seed alone would have moved the lumps around a world of the same colour.
 */
function pickMission(ctx: ScreenContext) {
  /*
   * The shared generator, not a hash written out by hand here.
   *
   * The hand-rolled one multiplied with `*` rather than `Math.imul`, so every product over 2^53 silently lost
   * its low bits - and the low bits are the entire output of an xorshift-multiply. Consecutive counters came
   * out correlated as a result: the first eight drives of a fresh save were three town errands, three red
   * canyons and two meadows, and the snow did not appear until the ninth. Measured, not guessed.
   */
  const rand = mulberry32(deriveSeed(ctx.save.routeCounter + 1, 'mission-pick'));

  return chooseMission(MISSIONS, {
    unlocks: ctx.save.unlocks,
    recentMissionIds: ctx.save.missionHistory.map((record) => record.missionId),
    rand,
  });
}
